import { useMemo, useState } from 'react';
import { cn } from '@klaro7/ui';
import type { AuditFinding, AuditLevel } from '../lib/seoAudit';

function scoreTone(score: number): { ring: string; text: string } {
  if (score < 50) return { ring: 'stroke-red-500', text: 'text-red-500' };
  if (score < 80) return { ring: 'stroke-amber-500', text: 'text-amber-500' };
  return { ring: 'stroke-emerald-500', text: 'text-emerald-500' };
}

/** Anello SVG animato 0-100. size px (default 96). Colore per fascia: <50 rosso, 50-79 ambra, >=80 verde smeraldo. Numero grande al centro + label "/100" piccola. */
export function SeoScoreRing({ score, size = 96 }: { score: number; size?: number }): JSX.Element {
  const clamped = Math.min(100, Math.max(0, score));
  const strokeWidth = Math.max(4, Math.round(size * 0.08));
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - clamped / 100);
  const tone = scoreTone(clamped);
  const center = size / 2;

  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          className="fill-none stroke-white/10"
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cn('fill-none transition-[stroke-dashoffset] duration-700 ease-out', tone.ring)}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('font-bold leading-none', tone.text)} style={{ fontSize: size * 0.28 }}>
          {clamped}
        </span>
        <span className="mt-0.5 text-[10px] font-medium leading-none text-white/60">/100</span>
      </div>
    </div>
  );
}

export interface SeoHeroStat {
  label: string;
  value: string | number;
  tone?: 'ok' | 'warn' | 'error';
}

const toneDotClass: Record<NonNullable<SeoHeroStat['tone']>, string> = {
  ok: 'bg-emerald-400',
  warn: 'bg-amber-400',
  error: 'bg-red-400',
};

/** Hero full-width: gradiente navy→teal (pattern del progetto), a sinistra titolo "SEO Studio" + sottotitolo, a destra ScoreRing + fila di stat chip (vetro: bg-white/10, testo bianco, bordo white/10; tone colora un puntino: verde/ambra/rosso). Responsive: su mobile impila. */
export function SeoHeroBanner({ score, stats, subtitle }: { score: number; stats: SeoHeroStat[]; subtitle?: string }): JSX.Element {
  return (
    <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-8">
      <div className="relative z-10 flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">SEO Studio</h1>
          {subtitle && <p className="mt-1 text-brand-cyan">{subtitle}</p>}
        </div>

        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
          <SeoScoreRing score={score} />
          {stats.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-white"
                >
                  {stat.tone && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', toneDotClass[stat.tone])} />}
                  <span className="text-sm font-semibold">{stat.value}</span>
                  <span className="text-xs text-white/70">{stat.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="absolute right-0 top-0 h-full w-1/3 opacity-10">
        <svg viewBox="0 0 200 200" className="h-full w-full" fill="none">
          <circle cx="160" cy="40" r="60" fill="white" />
          <circle cx="100" cy="140" r="80" fill="white" />
          <circle cx="180" cy="160" r="40" fill="white" />
        </svg>
      </div>
    </div>
  );
}

const levelBadgeConfig: Record<AuditLevel, { label: string; className: string }> = {
  error: { label: 'Errore', className: 'bg-red-50 text-red-700 border border-red-200' },
  warn: { label: 'Attenzione', className: 'bg-amber-50 text-amber-700 border border-amber-200' },
  ok: { label: 'OK', className: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
};

function LevelBadge({ level }: { level: AuditLevel }): JSX.Element {
  const config = levelBadgeConfig[level];
  return (
    <span className={cn('inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium', config.className)}>
      {config.label}
    </span>
  );
}

function CountBadge({ count, level }: { count: number; level: AuditLevel }): JSX.Element | null {
  if (count === 0) return null;
  const config = levelBadgeConfig[level];
  return (
    <span className={cn('inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-semibold', config.className)}>
      {count} {config.label.toLowerCase()}
    </span>
  );
}

interface AuditArea {
  area: string;
  findings: AuditFinding[];
  counts: Record<AuditLevel, number>;
}

function groupByArea(findings: AuditFinding[]): AuditArea[] {
  const map = new Map<string, AuditFinding[]>();
  for (const finding of findings) {
    const list = map.get(finding.area);
    if (list) list.push(finding);
    else map.set(finding.area, [finding]);
  }
  return Array.from(map.entries()).map(([area, areaFindings]) => {
    const counts: Record<AuditLevel, number> = { error: 0, warn: 0, ok: 0 };
    for (const finding of areaFindings) counts[finding.level] += 1;
    return { area, findings: areaFindings, counts };
  });
}

function AreaAccordion({ area, defaultOpen }: { area: AuditArea; defaultOpen: boolean }): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-neutral-50"
        aria-expanded={open}
      >
        <div className="flex min-w-0 items-center gap-3">
          <svg
            className={cn('h-4 w-4 shrink-0 text-neutral-400 transition-transform duration-200', open && 'rotate-90')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="truncate font-medium text-neutral-900">{area.area}</span>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          <CountBadge count={area.counts.error} level="error" />
          <CountBadge count={area.counts.warn} level="warn" />
          <CountBadge count={area.counts.ok} level="ok" />
        </div>
      </button>
      {open && (
        <ul className="divide-y divide-neutral-100 border-t border-neutral-200">
          {area.findings.map((finding, index) => (
            <li key={index} className="flex items-start gap-3 px-4 py-3">
              <LevelBadge level={finding.level} />
              <span className="text-sm text-neutral-700">{finding.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Pannello risultati audit raggruppati per `finding.area`: accordion per area (header con nome area + conteggi err/warn/ok come mini-badge, aperte di default le aree con errori). Righe con badge livello (Errore rosso / Attenzione ambra / OK smeraldo) e messaggio. Toggle "Mostra anche i controlli superati" (checkbox) governato da showOk/onToggleOk. Se nessun finding visibile: stato vuoto positivo con ✓. Tema CHIARO (bg-white, border-neutral-200). */
export function AuditFindingsPanel({
  findings,
  showOk,
  onToggleOk,
}: {
  findings: AuditFinding[];
  showOk: boolean;
  onToggleOk: (v: boolean) => void;
}): JSX.Element {
  const visibleFindings = useMemo(
    () => (showOk ? findings : findings.filter((f) => f.level !== 'ok')),
    [findings, showOk],
  );
  const areas = useMemo(() => groupByArea(visibleFindings), [visibleFindings]);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-neutral-900">Controlli SEO</h2>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-neutral-600">
          <input
            type="checkbox"
            checked={showOk}
            onChange={(e) => onToggleOk(e.target.checked)}
            className="h-4 w-4 rounded border-neutral-300 text-brand-teal-600 focus:ring-brand-teal-500"
          />
          Mostra anche i controlli superati
        </label>
      </div>

      {areas.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-emerald-200 bg-emerald-50 py-10 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-xl text-emerald-600">
            ✓
          </span>
          <p className="font-medium text-emerald-700">Nessun problema da segnalare</p>
          <p className="text-sm text-emerald-600/80">Tutti i controlli SEO risultano superati.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {areas.map((area) => (
            <AreaAccordion key={area.area} area={area} defaultOpen={area.counts.error > 0} />
          ))}
        </div>
      )}
    </div>
  );
}
